import { Wrench } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';

type NoToolsProps = {
  onAddClick?: () => void;
  onImportClick?: () => void;
  isLoading?: boolean;
  title?: string;
  description?: string;
};

export function NoTools({
  onAddClick,
  onImportClick,
  isLoading = false,
  title,
  description,
}: NoToolsProps) {
  const { t } = useTranslation();
  const displayTitle = title ?? (t('NO_TOOLS_YET') || 'No Tools Yet');
  const displayDescription =
    description ??
    (t('NO_TOOLS_YET_DESCRIPTION') ||
      "You haven't created any tools yet. Create your first tool to get started!");

  if (isLoading) {
    return (
      <Card className="border-none p-0 shadow-none">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Skeleton className="mb-6 h-20 w-20 rounded-full" />
          <Skeleton className="mb-2 h-7 w-40" />
          <Skeleton className="mb-2 h-5 w-80" />
          <Skeleton className="mb-8 h-5 w-72" />
          <Skeleton className="h-10 w-28 rounded-md" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-none p-0 shadow-none">
      <CardContent className="flex flex-col items-center justify-center py-12">
        <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-muted">
          <Wrench className="h-10 w-10 text-muted-foreground" />
        </div>

        <h3 className="mb-2 text-xl font-semibold">{displayTitle}</h3>
        <p className="mb-8 text-center text-base leading-relaxed text-muted-foreground">
          {displayDescription}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          {onImportClick ? (
            <Button variant="outline" onClick={onImportClick}>
              {t('IMPORT_FROM_JSON') || 'Import from JSON'}
            </Button>
          ) : null}
          {onAddClick ? (
            <Button onClick={onAddClick}>{t('ADD_TOOL') || 'Add Tool'}</Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
